const normalizeSlashPath = (value = "") =>
  String(value || "")
    .replace(/\\/g, "/")
    .replace(/^\/+|\/+$/g, "")
    .replace(/\/{2,}/g, "/");

const getFilenameFromPath = (value = "") => {
  const normalized = normalizeSlashPath(value);
  if (!normalized) return "";

  const segments = normalized.split("/");
  return segments[segments.length - 1] || "";
};

const removeKnownCasePrefixes = (value = "", caseId = "") => {
  const normalized = normalizeSlashPath(value);
  if (!normalized) return "";

  const prefixes = [
    caseId ? `cases/${caseId}/` : "",
    "cases/",
  ].filter(Boolean);

  for (const prefix of prefixes) {
    if (normalized.startsWith(prefix)) {
      return normalized.slice(prefix.length);
    }
  }

  return normalized;
};

const removeRootPrefix = (value = "", rootPrefix = "") => {
  const normalized = normalizeSlashPath(value);
  const normalizedRoot = normalizeSlashPath(rootPrefix);

  if (!normalized || !normalizedRoot) {
    return normalized;
  }

  if (normalized === normalizedRoot) {
    return "";
  }

  if (normalized.startsWith(`${normalizedRoot}/`)) {
    return normalized.slice(normalizedRoot.length + 1);
  }

  return normalized;
};

const splitBaseAndExtension = (filename = "") => {
  const safeName = String(filename || "").trim();
  const dotIndex = safeName.lastIndexOf(".");

  if (dotIndex <= 0) {
    return { base: safeName, extension: "" };
  }

  return {
    base: safeName.slice(0, dotIndex),
    extension: safeName.slice(dotIndex),
  };
};

const looksLikeStorageId = (value = "") => {
  const safeValue = String(value || "").trim();
  if (!safeValue) return false;

  if (/^\d+$/.test(safeValue)) return true;
  if (/^[a-f0-9]{8,}$/i.test(safeValue)) return true;
  if (/^[A-Za-z0-9_-]{8,}$/.test(safeValue) && /\d/.test(safeValue)) return true;

  return false;
};

const parseOriginalSegments = (segments = []) => {
  if (!segments.length) {
    return {
      storageDirectorySegments: [],
      logicalDirectorySegments: [],
      fileId: "",
      fileName: "",
    };
  }

  const fileName = segments[segments.length - 1] || "";
  const directorySegments = segments.slice(0, -1);
  const fileIdCandidate = directorySegments[directorySegments.length - 1] || "";
  const fileId = looksLikeStorageId(fileIdCandidate) ? fileIdCandidate : "";
  const logicalDirectorySegments = fileId
    ? directorySegments.slice(0, -1)
    : directorySegments;

  return {
    storageDirectorySegments: directorySegments,
    logicalDirectorySegments,
    fileId,
    fileName,
  };
};

const parseOcrSegments = (segments = []) => {
  if (!segments.length) {
    return {
      storageDirectorySegments: [],
      logicalDirectorySegments: [],
      fileId: "",
      fileName: "",
    };
  }

  const fileName = segments[segments.length - 1] || "";
  const directorySegments = segments.slice(0, -1);
  const { base } = splitBaseAndExtension(fileName);

  let fileId = "";
  const dashedMatch = base.match(/-([A-Za-z0-9_-]{6,})$/);
  if (dashedMatch?.[1]) {
    fileId = dashedMatch[1];
  } else if (looksLikeStorageId(base)) {
    fileId = base;
  }

  return {
    storageDirectorySegments: directorySegments,
    logicalDirectorySegments: directorySegments,
    fileId,
    fileName,
  };
};

export const parseCaseDocumentPath = ({ path = "", caseId = "", rootPrefix = "" } = {}) => {
  const normalizedPath = normalizeSlashPath(path);
  if (!normalizedPath) {
    return {
      normalizedPath: "",
      pathType: "unknown",
      fileId: "",
      fileName: "",
      relativePath: "",
      storageRelativePath: "",
      directoryPath: "",
      storageDirectoryPath: "",
      originalKey: "",
      ocrKey: "",
    };
  }

  const rootTrimmedPath = removeRootPrefix(normalizedPath, rootPrefix);
  const withoutCasePrefix = removeKnownCasePrefixes(rootTrimmedPath || normalizedPath, caseId);
  const pathSegments = withoutCasePrefix.split("/").filter(Boolean);
  const firstSegment = pathSegments[0] || "";
  const rootSegment = normalizeSlashPath(rootPrefix).split("/").filter(Boolean).pop() || "";

  const originalsMatch =
    firstSegment === "originals" ||
    firstSegment === "Originals" ||
    rootSegment === "originals" ||
    rootSegment === "Originals";

  const ocrMatch = firstSegment === "ocr-files" || rootSegment === "ocr-files";

  let contentSegments = pathSegments;
  if (firstSegment === "originals" || firstSegment === "Originals" || firstSegment === "ocr-files") {
    contentSegments = pathSegments.slice(1);
  }

  const parsed = originalsMatch
    ? parseOriginalSegments(contentSegments)
    : ocrMatch
      ? parseOcrSegments(contentSegments)
      : parseOriginalSegments(contentSegments);

  const relativePath = [...parsed.logicalDirectorySegments, parsed.fileName]
    .filter(Boolean)
    .join("/");
  const storageRelativePath = [...parsed.storageDirectorySegments, parsed.fileName]
    .filter(Boolean)
    .join("/");

  const directoryPath = parsed.logicalDirectorySegments.join("/");
  const storageDirectoryPath = parsed.storageDirectorySegments.join("/");

  return {
    normalizedPath,
    pathType: originalsMatch ? "original" : ocrMatch ? "ocr" : "unknown",
    fileId: parsed.fileId,
    fileName: parsed.fileName,
    relativePath,
    storageRelativePath,
    directoryPath,
    storageDirectoryPath,
    originalKey: originalsMatch ? normalizedPath : "",
    ocrKey: ocrMatch ? normalizedPath : "",
  };
};

export { getFilenameFromPath, normalizeSlashPath };
